/*
This is the data model for representing the current group/outcome's quantifiable data and associated
variance, analyzability, pooling, etc. This reactively responds to changes in publications, timepoint, etc.
*/
import {
	analysisSelections,
	analysisType,
	analyzeTreatmentAs,
	studySelections,
	selectedStudies,
} from 'model/SearchQuery'
import { screening } from 'model/Screening'
import { Variable, VBoolean, VArray, all, not, reactive } from 'alkali'
import { selectedTreatments, selectedOutcomes, selectedOutcomeIds } from 'model/EvidenceSearch'
import { computeEffect, outcomeMeasureNameForSettings } from 'analysis/Effect'
import studyUrls, { lookupSource } from '../components/studies/studyUrls'
import { uniq, sortBy, escape } from 'lodash'
import { getUnitType } from 'analysis/RowTypeUnit'
import { assignGroupArms } from 'analysis/assignGroupArms'
import { voteForArticle } from './Screening'
import { studyCollection, findStudyById } from './StudyCollection'

const COHORT_MEASURES = {
	RR: 'PLN',
	OR: 'PLO',
	RD: 'PR',
}

export const computeComparativeEffect = (outcome, groups) => {
	let analysis = analysisSelections.valueOf()
	let { interventionGroups, controlGroups } = groups
	return computeEffect({outcome, analysis, groups, ...groups})
}
/*const continuityCorrectionFactor = 12
const binaryToSE = (percent, N) =>
	Math.sqrt(
		((percent + continuityCorrectionFactor / N) *
			(100 - percent + continuityCorrectionFactor / N)) /
			N
	)*/

export function getOutcomeValue(outcome, group) {
	let cells = outcome?.cells
	let cell = cells && group && cells.find((cell) => cell.group == group.Id)
	if (!cell) return
	let N = cell?.N || group.N
	let ciWidth = getCIWidth()
	let se = cell.se
	let number = cell.adjustedNumber != undefined ? cell.adjustedNumber : cell.number != undefined ? cell.number : cell.estimatedFromAM != undefined ? { estimated : cell.estimatedFromAM } : undefined
	if (N && outcome.type == 'Binary') {
		let count = cell.count
		if (count > -1)
			number = (cell.count / N) * 100
		else
			count = number * N / 100
		let settings = analysisSelections.get('settings')
		if (count == 0) {
			let continuityCorrection = settings?.continuityCorrection ?? 0.5
			count += continuityCorrection
			N += continuityCorrection * 2
		}
		// this is basically the CI based on continuity corrected risk ratio variance
		// we could potentially go further and compute this based on current measure (OR).
		let errorRatio = Math.exp(Math.sqrt(1/count + (settings?.measureBin == 'RR' ? 1/N : -1/N)) * ciWidth)
		let ccPercent = count / N * 100
		return { value: number, lower: Math.min(ccPercent / errorRatio, number), upper: Math.min(ccPercent * errorRatio, 100) }
	}
	if (N && outcome.type == 'Incidence' && !se && cell.count) {
		let errorRatio = Math.exp(Math.sqrt(1 / cell.count) * ciWidth)
		return { value: number, lower: number / errorRatio, upper: number * errorRatio }
	}
	return { value: number, lower: number - se * ciWidth, upper: number + se * ciWidth }
}

export const identifyGroupArms = (study, groups) => {
	let outcomeSelections = studySelections.property(study.parentStudy?.id || study.id)
	let groupAnalyzeAs = outcomeSelections.get('groupAnalyzeAs')

	for (let { groupId, analyzeLabel } of groupAnalyzeAs || []) {
		for (let group of groups) {
			if (group.Id == groupId) {
				group.analyzeAs = analyzeLabel
			}
		}
	}
	return groups
}

export const identifyGroups = (study, groups) => {
	identifyGroupArms(study, groups)
	let selectedInterventions = selectedTreatments.valueOf()
	if (!(selectedInterventions?.length > 0) && analysisType != 'cohort') return groups
	let interventionLabels = selectedInterventions.map(
		(selectedIntervention) =>
			analyzeTreatmentAs.valueOf().get(selectedIntervention.id) || selectedIntervention.label
	)
	let interventionGroups, interventionLabel
	for (interventionLabel of interventionLabels) {
		// find matching group for first label we can find
		interventionGroups = groups.filter(
			(group) => group.analyzeAs && interventionLabel == group.analyzeAs
		)
		if (interventionGroups.length > 0) break
	}
	let controlLabel
	let controlGroups = groups.filter((group) => {
		if (group.analyzeAs) {
			if (controlLabel) return group.analyzeAs == controlLabel
			else if (group.analyzeAs && group.analyzeAs != interventionLabel) {
				controlLabel = group.analyzeAs
				return true
			}
		}
	})
	for (let group of groups) {
		if (group.isComparing)
			group.isComparing = false
		if (group.analyzeAs && group.ParentGroup) {
			let parent = groups.find(parent => parent.Id == (group.ParentGroup.Id || group.ParentGroup))
			if (parent)
				parent.subgroupAnalyzeAs = true
		}
	}
	for (let group of [...interventionGroups || [], ...controlGroups])
		group.isComparing = true
	groups = sortBy(groups, (group) => group.score ?
		group.analyzeAs ? group.analyzeAs == 'Control' ? -group.score - 1 : -group.score - 2 : -group.score : 1000)
	groups.interventionGroups = interventionGroups
	if (!interventionGroups)
		groups.interventionGroups = controlGroups
	else
		groups.controlGroups = controlGroups
	return groups
}

export const numToString = (x) =>
	typeof x == 'number'
		? isNaN(x) ? '?' :
			x == Infinity ? '∞'
			: x
					.toFixed(Math.min(Math.max(-Math.floor(Math.log10(Math.abs(x))) + 2, 0), 8))
					.replace(/\.(\d*?)0+$/, (t, mantissa) => (mantissa ? '.' + mantissa : ''))
		: x

export const outcomeMeasureName = (outcome, fullLabel?) =>
	analysisSelections.settings.to(settings => {
		return outcomeMeasureNameForSettings(outcome, settings, analysisType, fullLabel)
	})
const Publication = Variable.with({
	groups: [],
	designs: [''],
	year: 0,
	title: '',
	N: 0,
	author: '',
	conditions: [],
	interventions: [],
	characteristics: [],
	outcomes: [],
})

export const srcAbbrv = (study) => {
	let source = study.source ?? (study.metadata.IsUploaded ? 'Manual Entry' : undefined)
	let srcdef
	if (source) {
		srcdef = lookupSource(source, false)
	} else {
		// if we have missing DatabaseType, try harder and infer source from relations
		srcdef = studyUrls(study)?.find((s) => s.srcdef)?.srcdef
		source = typeof srcdef?.sources?.[0] == 'string' ? srcdef?.sources?.[0] : srcdef?.label
	}
	const srcabbrv = srcdef?.abbrv || srcdef?.label || source
	return {
		source,
		srcabbrv,
	}
}

let updateVotes
// updateVotes function definition located in Study.tsx
export function setUpdateVotes(newHandler) { updateVotes = newHandler }
export function getPublicationId(studyId) {
	let studySettings = studySelections.property(studyId)
	return studySettings.property('publication').to(
		(publicationId) => publicationId || studyId,
		async (publicationId) => {
			let currentStudyObject = await findStudyById([publicationId, studyCollection])
			let old = this.currentPublication?.valueOf()
			if (!currentStudyObject) {
				setTimeout(() => { // alternative to findStudyById: wait for the new study object
					currentStudyObject = this.currentPublication?.valueOf()
					updateVotes(old, currentStudyObject)
				}, 800)
			} else {
				updateVotes(old, currentStudyObject)
			}
			return studySettings.set('publication', publicationId)
		}
	)
}
export class StudyAnalysisModel {
	constructor(study) {
		this.study = study
		let { name, id, source } = study
		this.studySettings = studySelections.property(id)
		let studySettings = this.studySettings = studySelections.property(id)
		let currentPublicationId = this.currentPublicationId = getPublicationId.call(this, id)

		this.publications = reactive({}).to(() => {
			let publicationOptions
			if (study.additionalStudies) {
				publicationOptions = [
					{
						id: study.id,
						text: (study.year || 'NR') + '  ' + study.name,
						...srcAbbrv(study),
					},
				]
				for (let additionalStudy of study.additionalStudies) {
					publicationOptions.push({
						id: additionalStudy.id,
						text: (additionalStudy.year || 'NR') + '  ' + additionalStudy.name,
						...srcAbbrv(additionalStudy),
					})
				}
			}
			return publicationOptions || study
		})
		let isFullyExpanded = (this.isFullyExpanded = reactive(false))
		let currentPublication = (this.currentPublication = all([currentPublicationId, this.publications], (id) => {
				isFullyExpanded.put(false) // reset expansion when changing publications
				if (study.id == id) return (this.study = study)
				return (this.study = study.additionalStudies?.find((study) => study.id == id) || study)
			})
			.as(Publication))
		this.nameTooltip = currentPublication.to(({ name, authors }) => truncateTitle(
				name +
					'\nAuthors: ' +
					(authors instanceof Array ? authors.join('|') : authors || '')
			))
		let groups = currentPublication.property('groups')
		let N = currentPublication.property('N')
		let author = currentPublication.property('author')
		let conditions = currentPublication.property('conditions')
		this.interventions = currentPublication
			.to((publication) => {
				return uniq(
					(publication.groups || []).flatMap((group) =>
						(group.Interventions || []).flatMap((intervention) =>
							(intervention.terms || []).map((terms) => terms[0])
						)
					)
				)
			})
			.as(VArray)

		let defaultOutcome = currentPublication.to((publication) => publication.outcomes?.[0]) // use the server selected outcome as the default
		let outcomeName = (this.outcomeName = all([
			studySettings.property('outcome'),
			currentPublication,
		]).to(
			([outcomeName, publication]) => {
				if (
					outcomeName &&
					publication.outcomes?.find((outcome) => outcome.name == outcomeName)
				) {
					// outcome name has been selected and exists in the current publication
					return outcomeName
				} else {
					// otherwise auto-select
					return publication.outcomes?.[0]?.name
				}
			},
			(outcomeName) => {
				studySettings.undefine('outcomeId')
				studySettings.set('outcome', outcomeName)
			}
		))
		let groupAnalyzeAs = this.groupAnalyzeAs = studySettings.property('groupAnalyzeAs')
		let timepoint = (this.timepoint = all([
			studySettings.property('timepoint'),
			currentPublication,
		]).to(
			([timepoint, publication]) => {
				if (
					timepoint &&
					publication.outcomes?.find((outcome) => (outcome.time?.days || -1) == timepoint)
				) {
					// outcome name has been selected and exists in the current publication
					return timepoint
				} else {
					// otherwise auto-select
					return publication.outcomes?.[0]?.time?.days || -1
				}
			},
			(timepoint) => {
				studySettings.undefine('outcomeId')
				studySettings.set('timepoint', timepoint)
			}
		))
		const outcomes = all([outcomeName, currentPublication], (outcomeName, publication) => {
			if (!publication.outcomes) return []
			return publication.outcomes.filter((outcome) => outcome.name == outcomeName)
		})
		let timepoints = (this.timepoints = outcomes.to((outcomes) => {
			let timepoints = new Map()
			for (let outcomeRow of outcomes) {
				timepoints.set(outcomeRow.time?.days || -1, outcomeRow.time?.description || 'N/A')
			}
			let results = Array.from(timepoints).map(([days, description]) => ({
				id: days,
				text: cleanupTimepointDescription(description),
				days,
			}))
			return sortBy(results, 'days')
		}))
		let outcome = (this.outcome = all([timepoint, outcomes], (timepoint, outcomes = []) => {
			// if selected timepoint is not in selected outcomes (e.g. we switched outcomes), reset to first outcome
			return outcomes.find(outcome => (outcome.time?.days || -1) == timepoint)
				|| outcomes[0]
		}))

		let groupsVar = this.groupsVar = all([groups, outcome, groupAnalyzeAs]).to(([groups, outcome]) => {
			assignGroupArms({
				groups, outcome,
				assignmentScores: this.study.assignmentScores,
				doAssignment: true,
				groupPooling: analysisSelections.settings.get('multiGroupPooling'),
				analyzableWeight: Math.pow((analysisSelections.sorting.get('analyzable') || 50) / 100, 0.25),
			})
			if (groups.some(group => group.ParentGroup)) { // if we have subgroups, we only show them when fully expanded
				this.getGroups()
				return isFullyExpanded.to(expanded =>
					identifyGroups(study, expanded ? groups :
						identifyGroupArms(study, groups).filter(group => !group.ParentGroup || group.analyzeAs)))
			}
			return identifyGroups(study, groups)
		}).as(VArray)

		this.selectedGroups = groupsVar.to(groups => {
			return groups.filter(group => group.analyzeAs || group.subgroupAnalyzeAs)
		})
		let selectedOutcome = selectedOutcomes.valueOf()?.[0] // TODO: Need to get loop through these in multiple outcome scenarios
		this.isPositiveDirectionality = Boolean(
			selectedOutcome?.isPositive || selectedOutcome?.suggestedPositive
		)
		let analyzable = (this.analyzable = groupsVar.to(() => {
			let { groups, isAnalyzable, reason } = this.getRowData() || {}
			let analyzableCount =
				groups &&
				new Set(groups.map((group) => group.analyzeAs).filter((analyzeAs) => analyzeAs)).size
			let row = outcome.valueOf()
			let isPotentiallyAnalyzable = analysisType == 'cohort' || analysisType == 'auto' || analysisType == 'evidence-table'
					? analyzableCount > 0
					: analyzableCount > 1
			if (isPotentiallyAnalyzable || analysisType == 'evidence-table') {
				if (!isAnalyzable) {
					return {
						isQualitative: true,
						isAnalyzable: false,
						isPotentiallyAnalyzable,
						reason: reason || (analysisType == 'evidence-table' ? 'This study does not have extracted outcome data to analyze (consider extracting data), but can be included as a reference in your evidence table.' : undefined),
					}
				}
				// else
				return {
					isAnalyzable,
					isPotentiallyAnalyzable,
					reason,
				}
			}
			
			let notAnalyzableGroupReason = reason
			// if the study has measurable outcomes - if not, how to get them
			let measurableOutcomes = study.hasAnalyzableData ? "Select treatment groups to include in your analysis. Click the box next to the group name to select a treatment assignment." : "This study needs to have extracted outcome data to analyze. Consider extracting data from this study."
			let selectReason = (selectedOutcomeIds.valueOf().length > 0 ) ? measurableOutcomes : "Select an outcome from the outcome filter to start building an analysis."
			if (!reason)
				study.groups?.some((group) => (notAnalyzableGroupReason = group.notAnalyzableReason))
			return {
				isAnalyzable: false,
				isPotentiallyAnalyzable: study.hasAnalyzableData,
				reason: study.outcomes
					? notAnalyzableGroupReason
						? notAnalyzableGroupReason
						: selectReason
					: 'This study needs to have extracted group and outcome data to analyze. Consider extracting data from this study.',
			}
		}))
		const { srcabbrv: pubAbbrv, source: pubSrc } = srcAbbrv(study)
		this.pubAbbrv = pubAbbrv
		this.pubSrc = pubSrc
	}
	getGroups() {
		let groups = this.study.groups
		if (!groups) return
		let outcome = this.outcome.valueOf()
		identifyGroupArms(this.study, groups)

		let treatments = selectedTreatments
			.valueOf()
			?.map((treatment) => analyzeTreatmentAs.valueOf().get(treatment.id) || treatment.label)
		if (!treatments) return
		return groups.filter((group) => group.analyzeAs)
	}
	getRowData() {
		let groups = this.getGroups(this.study)
		if (!groups || groups.length == 0) return
		let row = this.outcome.valueOf()
		if (!row) {
			return {
				reason: 'No row data',
			}
		}
		let selectedOutcome = selectedOutcomes.valueOf()?.[0] // TODO: Need to get loop through these in multiple outcome scenarios
		if (!selectedOutcome) return {}

		let participantError = (groups.some(group => group.N) || row.cells?.some(cell => cell.N)) ? '' : 'The number of participants was not reported. '
		if (!analysisSelections.settings.get('useSMD')) {
			if (getUnitType(row) != getUnitType(selectedOutcome)) {
				return {
					reason:
						participantError +
						' This study is incompatible with your current analysis. The study reports ' + 
						getUnitType(row) +
						', and your current analysis is in ' +
						getUnitType(selectedOutcome) +
						'. Click the units dropdown above the evidence summary to update.',
				}
			} else if (!row.matchingTerm) {
				return {
					groups,
					reason:
						participantError +
						'This analysis is for ' +
						selectedOutcome.label +
						', and does not match this outcome.' +
						' However, it can still be selected for qualitative comparison.',
				}
			}
		}
		if (participantError) {
			return { reason: participantError }
		}

		let cells = groups
			.map((group) => {
				let cells = row?.cells
				return cells && group && cells.find((cell) => cell.group == group.Id)
			})
			.filter((cell) => cell)
		let reason
		let isAnalyzable: string|boolean = !cells.some((cell) => {
			let issue = analyzableVerification(cell, row)
			if (!issue)
				return
			let { isAnalyzable, reason: cellReason } = issue
			if (cellReason)
				reason = cellReason
			return !isAnalyzable
		})
		if (isAnalyzable && reason?.startsWith('Variance was not reported'))
			isAnalyzable = 'needs-imputed-variance'
		return {
			study: this.study,
			row,
			groups,
			cells,
			isAnalyzable,
			reason,
		}
	}
	recordStudySelections() {
		this.studySettings.set('outcome', this.outcomeName.valueOf())
		this.studySettings.set('outcomeId', this.outcome.get('id'))
		this.studySettings.set('timepoint', this.timepoint.valueOf())
		let groupAnalyzeAs = this.studySettings.get('groupAnalyzeAs')?.slice(0) || []

		let groups = this.getGroups(this.study)
		let hasGroupsChange
		for (let group of groups || []) {
			if (group.analyzeAs && !groupAnalyzeAs.find(({ groupId }) => groupId == group.Id)) {
				hasGroupsChange = true
				groupAnalyzeAs.push({
					groupId: group.Id,
					analyzeLabel: group.analyzeAs,
					autoAssigned: true,
				})
			}
		}
		if (hasGroupsChange)
			this.studySettings.set('groupAnalyzeAs', groupAnalyzeAs)
	}
}
const truncateTitle = (string) => (string.length > 165 ? string.slice(0, 160) + '...' : string)
export const cleanupTimepointDescription = (description) =>
	description.replace(/Baseline\s*-\s*/, '').replace(/\[.*\]/g, '')

function analyzableVerification(cell, row) {
	if (cell.AMs)
		return
	if (!cell && typeof cell.number == 'number' && !isNaN(cell.number)) {
		return { isAnalyzable: false, reason: 'There was no value reported for outcome(s).' }
	}
	if (row.type == 'Binary') {
		if (cell.number > 100 || cell.count > cell.N)
			return { isAnalyzable: false, reason: 'Greater than 100% of participants reported, which is invalid' }
		if (cell.number < 0 || cell.count < 0)
			return { isAnalyzable: false, reason: 'Less than zero participants reported, which is invalid' }
	}

	if (!(row.type == 'Binary' || row.dataType == 'Percentage' || (row.type == 'Incidence' ? cell.totalPersonTime : (cell.sd > 0 || cell.se > 0)))) {
		if (cell.sd < 0 || cell.se < 0)
			return { isAnalyzable: false, reason: 'There was a negative variance reported for outcome(s).' }
		return { isAnalyzable: false, reason: "Variance was not reported for this study's outcome data. However, DocAnalytics can estimate the variance if other similar studies are included. Ensure that other studies with similar outcomes are selected."
		}
	}
}
export function getCIWidth() {
	let confidenceIntervalWidthUnit
	let getUnit = {'95': 1.96, '90': 1.645, '99': 2.58}
	let confidenceLevel = analysisSelections.get('settings')?.confidenceLevel
	if (confidenceLevel) {
		confidenceIntervalWidthUnit = getUnit[confidenceLevel]
	}
	else {
		confidenceIntervalWidthUnit = 1.96
	}
	return confidenceIntervalWidthUnit
}

